// Global
import { RichText, RichTextField } from '@sitecore-content-sdk/nextjs';
import { RichTextProps } from '@sitecore-content-sdk/nextjs/types/components/RichText';
import React, { JSX, useEffect, useState } from 'react';
import { PageTemplateIds } from 'lib/constants';

// Local
import useIsEditing from 'lib/hooks/useIsEditing';
import { useFieldWithFallbacks } from 'lib/hooks/useFieldWithFallbacks';

interface RichTextWrapperProps extends RichTextProps, React.HTMLAttributes<HTMLDivElement> {
  selected?: boolean;
  fallbacks?: (RichTextField | undefined)[];
  page?: {
    templateId?: string;
  };
}

const RichTextWrapper = ({
  field,
  fallbacks,
  editable = true,
  className,
  pageTemplateId: pageTemplateId,
  ...props
}: RichTextWrapperProps): JSX.Element => {
  const isEditing = useIsEditing() && editable;

  const { renderField } = useFieldWithFallbacks(field, fallbacks);

  const updatedField = useUpdatedRichTextField({ field: renderField, editable });

  const hasValue = !!updatedField?.value;

  const isTeachingSheetPage = pageTemplateId === PageTemplateIds.teachingSheetPage;
  const rteClassName = isTeachingSheetPage ? 'rte smallHeadings' : 'rte';

  // We should only render if it has a value, or if we are editing / selected in the editor.
  const shouldRender = hasValue || isEditing || pageTemplateId;

  if (!shouldRender) return <></>;

  // In editing mode, move `className` to wrapping div,
  // because Sitecore doesn't render the props if field is empty,
  // and so the "rte" and other css classes are missing initially.
  if (isEditing) {
    return (
      <div className={`${rteClassName} ${className ?? ''}`}>
        <RichText
          {...props}
          data-component="helpers/fieldwrappers/richtextwrapper"
          editable={editable}
          field={updatedField}
          pageTemplateId={pageTemplateId}
        />
      </div>
    );
  }
  return (
    <RichText
      {...props}
      internalLinksSelector={'hover'}
      className={`${rteClassName} ${className ?? ''}`}
      data-component="helpers/fieldwrappers/richtextwrapper"
      editable={editable}
      field={updatedField}
      prefetchLinks={false}
      pageTemplateId={pageTemplateId}
    />
  );
};

const NEW_TAB_ICON_STRING = `<span class="svg-icon inline-flex align-middle -ml-2 h-6 w-6">
    <svg
      aria-hidden="true"
      class="inline ml-2 -mt-1 h-em w-em"
      fill="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M8.25 3.75H19.5a.75.75 0 01.75.75v11.25a.75.75 0 01-1.5 0V6.31L5.03 20.03a.75.75 0 01-1.06-1.06L17.69 5.25H8.25a.75.75 0 010-1.5z"
        clipRule="evenodd"
        fillRule="evenodd"
      ></path>
    </svg>
  </span>`;

function useUpdatedRichTextField({ field, editable = true }: RichTextProps) {
  const isEditing = useIsEditing() && editable;

  const [content, setContent] = useState(field?.value);

  const updatedField: RichTextField = {
    value: content || '',
  };

  // Run this client-side because we don't have access to the document server-side
  useEffect(() => {
    const template = document.createElement('template');
    template.innerHTML = field?.value || '';

    const links = [...template.content.querySelectorAll('a')];
    links.forEach((a) => {
      const href = a.getAttribute('href') || '';
      const isPdf = /\.pdf(\?|#|$)/i.test(href);
      const isExternal = href.startsWith('http') || a.getAttribute('target') === '_blank';
      const hasImage = a.querySelector('img') !== null;

      if (isPdf) {
        // PDF link: add screen reader text and PDF label
        a.innerHTML = `${a.innerHTML}<span class="sr-only"> (Opens in a new tab)</span> <span>(PDF)</span>`;
      } else if (hasImage) {
        // Images look silly with the "external tab" icon, so we don't add it to links that contain images.
        const svgIcon = a.querySelector('.svg-icon');
        if (svgIcon) {
          svgIcon.remove();
        }
        a.innerHTML = `${a.innerHTML}<span class="sr-only"> (Opens in a new tab!)</span>`;
      } else if (isExternal) {
        // External link: set to open in new tab, add screen reader text and icon
        a.setAttribute('target', '_blank');
        a.innerHTML = `${a.innerHTML}<span class="sr-only"> (Opens in a new tab)</span> ${NEW_TAB_ICON_STRING}`;
      }
    });

    // Update the content
    setContent(template.innerHTML);
  }, [field?.value]);

  return isEditing ? field : updatedField;
}

export default RichTextWrapper;
